import { clearComponentError } from '../components/ComponentError/redux';
import { setNotification } from '../redux/slices/notifications';
import { logger } from './logger';
import { ValidationError } from '../services/ValidationError';
import { ErrorSeverity } from '../enums/ErrorSeverity';
import { handleApiFailure } from './handleApiFailure';
import { mapValidationErrorsToFormErrors, setFormGeneralError } from '../components/Form/redux';

const defaultSuccessResult = { wasSuccessful: true, validationErrors: null };

const defaultWasSuccessful = (result) => !!result?.wasSuccessful;

const defaultOptions = {
    errorSeverity: ErrorSeverity.ERROR, // Default to ERROR severity
    shouldShowErrorDialog: false, // Kept for backward compatibility
    shouldShowErrorToast: true, // Kept for backward compatibility
    shouldAutoDismissNotification: true,
    useUserFriendlyErrors: true,
    wasSuccessful: defaultWasSuccessful,
};

/**
 * Handles API calls with comprehensive error handling and user notification
 *
 * @param {Function} asyncApiFn - async api function, can be one method or multiple which are awaited on
 * @param {Object} options - options for processing the API call
 * @param {Function} options.dispatch - required - reference to the dispatch function
 * @param {string} options.successMessage - optional - if successful a toast notification will be dispatched
 * @param {string} options.errorMessage - optional - if unsuccessful, error message to prepend to the error message from API
 * @param {number} options.errorSeverity - optional - determines how the error is displayed (uses ErrorSeverity enum)
 * @param {boolean} options.mapFormErrors - optional - if true, map validationErrors to the redux form errors state (independent of componentId)
 * @param {string} options.slice - required if mapFormErrors is true - location of redux form slice
 * @param {string} options.prefix - optional - use if mapFormErrors is true - location of redux form slice
 * @param {string} options.componentId - optional - if provided, errors will be displayed at the component level (independent of mapFormErrors)
 * @param {boolean} options.captureFormGeneralError - optional (default is false) - if true, general error will be put in FORM_GENERAL_ERRORS_KEY in redux
 * @param {boolean} options.useUserFriendlyErrors - optional (default is true) - if true, generic errors will be replaced with more user-friendly messages
 * @param {boolean} options.shouldAutoDismissNotification - optional (default is true) - if true, success or error message will automatically disappear after a few moments
 * @param {string} options.toastPosition - optional - placement for toast notifications from this API call
 * @param {Object} options.validationMapping - optional - mapping of validation errors to form fields
 * @param {boolean} options.shouldValidateNonExistentFields - optional - whether to validate fields that don't exist in the form
 * @param {Function} options.wasSuccessful - optional - function to determine if the API response was successful (default checks result.wasSuccessful)
 * @param {boolean} options.shouldShowErrorDialog - DEPRECATED - use errorSeverity: ErrorSeverity.CRITICAL instead
 * @param {boolean} options.shouldShowErrorToast - DEPRECATED - use errorSeverity parameter instead
 *
 */
export async function handleApiCall(asyncApiFn, options) {
    if (!options) throw new Error("'options' argument is required.");
    if (!options.dispatch) throw new Error("'dispatch' must be provided in the options object.");

    const hasExplicitErrorSeverity = Object.prototype.hasOwnProperty.call(options, 'errorSeverity');
    const hasExplicitShouldShowErrorToast = Object.prototype.hasOwnProperty.call(
        options,
        'shouldShowErrorToast',
    );

    options = Object.assign({}, defaultOptions, options);

    const {
        componentId,
        dispatch,
        errorMessage,
        mapFormErrors,
        prefix,
        shouldShowErrorDialog,
        shouldShowErrorToast,
        shouldValidateNonExistentFields,
        slice,
        successMessage,
        toastPosition,
        validationMapping,
        useUserFriendlyErrors,
        errorSeverity,
        wasSuccessful,
    } = options;

    const effectiveSeverity = getEffectiveSeverity({
        errorSeverity,
        shouldShowErrorDialog,
        shouldShowErrorToast,
        defaultOptions,
        componentId,
        hasExplicitErrorSeverity,
        hasExplicitShouldShowErrorToast,
    });

    let result;

    if (componentId) await dispatch(clearComponentError({ componentId }));

    try {
        result = await asyncApiFn();
        if (!result) {
            logger.warn('asyncApiFn did not return a valid result.');
            result = defaultSuccessResult;
        }

        const wasSuccessfulResult = wasSuccessful(result);

        if (!wasSuccessfulResult) {
            let hadValidationErrors = false;

            // Handle form error mapping if requested
            if (mapFormErrors) {
                if (!slice)
                    throw new Error(
                        "'slice' must be provided in the options object when 'mapFormErrors' is true.",
                    );

                const validationErrors = result?.validationErrors;

                if (validationErrors && Object.keys(validationErrors).length > 0) {
                    hadValidationErrors = true;

                    // A general (non-field) error captured into the form's general-error
                    // slot must NOT also be handed to the mapper, where its lack of a
                    // matching field would route it to a duplicate toast (same
                    // duplicate-display class as CCW-18392).
                    const capturesGeneralError =
                        !!options.captureFormGeneralError && !!validationErrors.general;
                    const mappableErrors = capturesGeneralError
                        ? Object.fromEntries(
                              Object.entries(validationErrors).filter(([key]) => key !== 'general'),
                          )
                        : validationErrors;

                    if (Object.keys(mappableErrors).length > 0) {
                        await dispatch(
                            mapValidationErrorsToFormErrors({
                                slice,
                                prefix,
                                validationErrors: mappableErrors,
                                validationMapping,
                                shouldValidateNonExistentFields,
                            }),
                        );
                    }

                    // the result may have a general error message, not tied to a specific field
                    if (capturesGeneralError) {
                        await dispatch(
                            setFormGeneralError({
                                slice,
                                prefix,
                                errorMessage: validationErrors.general,
                            }),
                        );
                    }
                }
            }

            // ValidationErrors handed to the form mapper are handled by the form flow:
            // matching fields render inline, and genuinely unmapped keys get a mapper toast.
            // Do not also raise a component-level retry error for user-input validation.
            if (!hadValidationErrors) {
                throw new ValidationError(result);
            }
        } else if (successMessage) {
            await dispatch(
                setNotification({
                    message: successMessage,
                    options: {
                        appearance: 'success',
                        autoDismiss: options.shouldAutoDismissNotification,
                        position: toastPosition,
                    },
                }),
            );
        }

        return result;
    } catch (err) {
        const errorResult = await handleApiFailure(err, {
            dispatch,
            errorMessage,
            useUserFriendlyErrors,
            effectiveSeverity,
            componentId,
            shouldAutoDismissNotification: options.shouldAutoDismissNotification,
            toastPosition,
            result,
        });

        return errorResult;
    }
}

/**
 * Determines the effective error severity based on options and legacy support
 * @param {Object} params - The parameters object
 * @param {number} params.errorSeverity - The error severity from options
 * @param {boolean} params.shouldShowErrorDialog - Legacy option for showing error dialog
 * @param {boolean} params.shouldShowErrorToast - Legacy option for showing error toast
 * @param {string} params.componentId - Component ID if specified
 * @param {Object} params.defaultOptions - Default options object
 * @param {boolean} params.hasExplicitErrorSeverity - Whether errorSeverity was provided
 * @param {boolean} params.hasExplicitShouldShowErrorToast - Whether shouldShowErrorToast was provided
 * @returns {number} The effective error severity value
 */
function getEffectiveSeverity({
    errorSeverity,
    shouldShowErrorDialog,
    shouldShowErrorToast,
    componentId,
    defaultOptions,
    hasExplicitErrorSeverity,
    hasExplicitShouldShowErrorToast,
}) {
    let effectiveSeverity = errorSeverity;

    // Check for legacy options and warn about deprecation
    if (shouldShowErrorDialog !== defaultOptions.shouldShowErrorDialog) {
        logger.warn(
            'DEPRECATION WARNING: shouldShowErrorDialog is deprecated. Use errorSeverity: ErrorSeverity.CRITICAL instead.',
        );
        effectiveSeverity = ErrorSeverity.CRITICAL;
    } else if (shouldShowErrorToast !== defaultOptions.shouldShowErrorToast) {
        logger.warn(
            'DEPRECATION WARNING: shouldShowErrorToast is deprecated. Use errorSeverity parameter instead.',
        );
        effectiveSeverity =
            shouldShowErrorToast === false ? ErrorSeverity.NONE : ErrorSeverity.ERROR;
    }

    // If componentId is provided and no notification/dialog display was explicitly requested,
    // default to NONE to avoid duplicate error notifications since component handles display.
    if (
        componentId &&
        !hasExplicitErrorSeverity &&
        !(hasExplicitShouldShowErrorToast && shouldShowErrorToast === true) &&
        shouldShowErrorDialog === defaultOptions.shouldShowErrorDialog &&
        shouldShowErrorToast === defaultOptions.shouldShowErrorToast
    ) {
        effectiveSeverity = ErrorSeverity.NONE;
    }

    return effectiveSeverity;
}

export const DEFAULT_PAGE_SIZE = 20;
